import cx from "classnames";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { useCurrentErrorMessage } from "../../helpers/errorHelpers";
import { isGameConceptHardRejected } from "../../helpers/gameConceptHelpers";
import { useProgramBackgroundColorPicker } from "../../helpers/useProgramColorPicker";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmGameConceptStatus } from "../../models/crmStatus";
import { OnboardingErrorType } from "../../models/errors";
import { Programs } from "../../models/programsConfig";
import {
  AaaApplicationStep,
  IdXboxApplicationStep,
  PitchApplicationStep,
} from "../../models/status";
import { AaaGameInformationFormId } from "../../pages/AaaGameInformationPage/AaaGameInformationPageConstants";
import { GameInformationFormId } from "../../pages/GameInformationPage/GameInformationPageConstants";
import { IdXboxIntroFormId } from "../../pages/IdXboxIntroPage/IdXboxIntroPageConstants";
import { PitchInformationFormId } from "../../pages/PitchInformationPage/PitchInformationPageConstants";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { crmActions } from "../../redux/actions/crmApiActions";
import {
  getAaaApplicationStep,
  getCurrentError,
  getCurrentGameConcept,
  getCurrentPitchConcept,
  getIdXboxApplicationStep,
  getIsSubmitting,
  getPitchApplicationStep,
} from "../../redux/selectors";
import { validateGameConcept } from "../../server/utils/dataValidation/dataFilter";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import { RightPaneBox } from "../RightPaneBox";
import { LoadingSpinner } from "../Spinner";
import * as styles from "./SubmitButton.module.css";

export type SubmitButtonProps = Readonly<{
  label: string;
  onSubmit: () => void;
  footNote?: string;
}>;

export const SubmitButton = ({ label, onSubmit, footNote }: SubmitButtonProps) => {
  const isSubmitting = useSelector(getIsSubmitting);
  const currentError = useSelector(getCurrentError);
  const currentErrorMessage = useCurrentErrorMessage();
  const programBackgroundColor = useProgramBackgroundColorPicker(true);

  return (
    <RightPaneBox>
      <Row className={styles.row}>
        {!isSubmitting && (
          <>
            <BasicButton
              as={"button"}
              buttonType={ButtonType.Brand}
              className={cx(styles.button, programBackgroundColor)}
              onClick={onSubmit}
            >
              {label}
            </BasicButton>
            {footNote && <span className={styles.termsAndConditions}>{footNote}</span>}
          </>
        )}
        {isSubmitting && <LoadingSpinner />}
        {currentError?.type === OnboardingErrorType.Submit && (
          <span className={styles.errorMessage}>{currentErrorMessage}</span>
        )}
      </Row>
    </RightPaneBox>
  );
};

export const IdXboxSubmitButton = () => {
  const dispatch = useDispatch();

  const [submitIntroduction, programSubmitTermsAndConditions] = useStrings(
    "submitIntroduction",
    "programSubmitTermsAndConditions"
  );

  const currentStep = useSelector(getIdXboxApplicationStep);

  if (currentStep !== IdXboxApplicationStep.IntroductionReadyToSubmit) {
    return null;
  }

  const onSubmitIntroduction = () => {
    logPageAction(OnboardingPageAction.SubmitIntroductionButton);
    dispatch(crmActions.submitCrmData.start(Programs.IdXbox, IdXboxIntroFormId, undefined));
  };

  return (
    <SubmitButton
      label={submitIntroduction}
      onSubmit={onSubmitIntroduction}
      footNote={programSubmitTermsAndConditions}
    />
  );
};

export const IdXboxGameConceptSubmitButton = () => {
  const dispatch = useDispatch();
  const [submitApplication, conceptSubmitTermsAndConditions] = useStrings(
    "submitApplication",
    "conceptSubmitTermsAndConditions"
  );
  const currentGameConcept = useSelector(getCurrentGameConcept);
  const [isSubmitButtonEnabled, setIsSubmitButtonEnabled] = useState(false);

  useEffect(() => {
    const isEnabled = async () => {
      const gameConceptStatus = currentGameConcept?.crmGameConceptStatus;
      const gameConceptSubStatus = currentGameConcept?.crmGameConceptSubStatus;
      const isHardRejected = isGameConceptHardRejected(gameConceptStatus, gameConceptSubStatus);
      if (
        !currentGameConcept?.forms ||
        gameConceptStatus === CrmGameConceptStatus.UnderReview ||
        gameConceptStatus === CrmGameConceptStatus.OnHold ||
        gameConceptStatus === CrmGameConceptStatus.AdditionalContractsSent ||
        gameConceptStatus === CrmGameConceptStatus.Accepted ||
        isHardRejected
      ) {
        setIsSubmitButtonEnabled(false);
        return;
      }

      const isReadyToSubmit = await validateGameConcept(currentGameConcept.forms);
      setIsSubmitButtonEnabled(isReadyToSubmit.result);
    };
    isEnabled();
  }, [currentGameConcept]);

  const onSubmitApplication = () => {
    logPageAction(OnboardingPageAction.SubmitApplicationButton);
    dispatch(
      crmActions.submitCrmData.start(Programs.IdXbox, GameInformationFormId, currentGameConcept?.id)
    );
  };

  return isSubmitButtonEnabled ? (
    <SubmitButton
      label={submitApplication}
      onSubmit={onSubmitApplication}
      footNote={conceptSubmitTermsAndConditions}
    />
  ) : null;
};

export const AaaSubmitButton = () => {
  const dispatch = useDispatch();
  const currentGameConcept = useSelector(getCurrentGameConcept);
  const [submitGameConcept, conceptSubmitTermsAndConditions] = useStrings(
    "submitGameConcept",
    "conceptSubmitTermsAndConditions"
  );
  const currentStep = useSelector(getAaaApplicationStep);
  if (currentStep !== AaaApplicationStep.ApplicationReadyForReview) {
    return null;
  }

  const onSubmitGameConcept = () => {
    logPageAction(OnboardingPageAction.SubmitAaaApplicationButton);
    dispatch(
      crmActions.submitCrmData.start(Programs.Aaa, AaaGameInformationFormId, currentGameConcept?.id)
    );
  };

  return (
    <SubmitButton
      label={submitGameConcept}
      onSubmit={onSubmitGameConcept}
      footNote={conceptSubmitTermsAndConditions}
    />
  );
};

export const PitchSubmitButton = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [submitApplication, conceptSubmitTermsAndConditions] = useStrings(
    "submitApplication",
    "conceptSubmitTermsAndConditions"
  );
  const currentStep = useSelector(getPitchApplicationStep);
  const currentPitchConcept = useSelector(getCurrentPitchConcept);

  if (currentStep === PitchApplicationStep.ApplicationSubmitted) {
    history.push(OnboardingUrls[RouteNames.PitchWelcome]);
    return null;
  }

  if (currentStep !== PitchApplicationStep.ApplicationReadyForReview) {
    return null;
  }

  const onSubmitApplication = () => {
    logPageAction(OnboardingPageAction.SubmitPitchApplicationButton);
    dispatch(
      crmActions.submitCrmData.start(
        Programs.Pitch,
        PitchInformationFormId,
        currentPitchConcept?.id
      )
    );
  };

  return (
    <SubmitButton
      label={submitApplication}
      onSubmit={onSubmitApplication}
      footNote={conceptSubmitTermsAndConditions}
    />
  );
};
